import { type ReactNode, useCallback } from "react";
import { type NumberFormatEvent } from "~/common/components/CustomNumberFormat";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { CURRENCY_FORMATTER } from "~/common/utils/currencyFormatter";
import { NumericFormat, type SourceInfo } from "react-number-format";
import { TextField } from "~/common/mui";

interface PriceInputProps {
  className?: string;
  value?: number;
  currencyIso?: string;
  label: ReactNode;
  helperLabel?: ReactNode;
  error?: boolean;
  disabled?: boolean;
  allowedLength?: number;

  onChange?: (e: NumberFormatEvent, sourceInfo: SourceInfo) => void;
  onBlur?: () => void;
  defaultCurrencyIso?: string;
  shrink?: boolean;
  "data-test-id"?: string;
  fullWidth?: boolean;
}

const PriceInput = ({
  allowedLength,
  onChange,
  "data-test-id": testId,
  currencyIso = getRuntimeConfig().currencyIso,
  fullWidth = true,
  ...rest
}: PriceInputProps) => {
  const currencyConfig = CURRENCY_FORMATTER[currencyIso] ?? {};

  const isAllowed = useCallback(
    (event: NumberFormatEvent) => {
      if (!allowedLength || !event.value) {
        return true;
      }

      return event.value.toString().length <= allowedLength;
    },
    [allowedLength],
  );

  return (
    <NumericFormat
      data-test-id={testId}
      {...currencyConfig}
      customInput={TextField}
      thousandSeparator={NF.getThousandSeparator()}
      decimalSeparator={NF.getDecimalSeparator()}
      fixedDecimalScale={true}
      decimalScale={NF.getFractionDigits(currencyIso)}
      isAllowed={isAllowed}
      onValueChange={onChange}
      fullWidth={fullWidth}
      {...rest}
    />
  );
};

export default PriceInput;
